import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { statusBadgeClass } from "../lib/status_badge"

export default class extends Controller {
  static targets = [
    "status",
    "content",
    // The shared stat card (shared/_stat_cards) — the count lands in its
    // .gl-stat-value; the partial owns that markup, so the target is the card.
    "zeroKbCard",
    "conversationIds",
    "reasonRows",
    "riskRows",
    // STATE-3 (#15910): the shared danger alert with a Retry button, revealed
    // when the load fails. Optional so a host without it still reads the
    // failure in the status line.
    "error",
  ]

  static values = {
    url: String,
  }

  connect() {
    this.load()
  }

  async load() {
    try {
      const response = await get(this.urlValue, { responseKind: "json" })

      if (!response.ok) throw new Error(`Request failed (${response.status})`)

      this.render(await response.json)
    } catch (error) {
      console.error("[SupportAdmin] coverage gaps failed:", error)
      this.statusTarget.textContent = "Could not load coverage signals."
      if (this.hasErrorTarget) {
        this.statusTarget.hidden = true
        this.errorTarget.hidden = false
      }
    }
  }

  retry() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
    this.statusTarget.textContent = "Loading coverage signals…"
    this.statusTarget.hidden = false
    this.load()
  }

  render(payload) {
    const zeroKb = payload.zero_kb_hit_conversations_24h || {}
    this.zeroKbCardTarget.querySelector(".gl-stat-value").textContent = String(zeroKb.count || 0)
    this.renderConversationIds(zeroKb.conversation_ids || [])
    this.renderRows(this.reasonRowsTarget, payload.by_reason_category || [])
    this.renderRows(this.riskRowsTarget, payload.by_risk_category || [])
    this.statusTarget.hidden = true
    this.contentTarget.hidden = false
  }

  // Plain id badges: there is no Conversation Review screen to open (#15910).
  renderConversationIds(ids) {
    this.conversationIdsTarget.innerHTML = ""
    if (ids.length === 0) {
      this.conversationIdsTarget.append(this.emptyText("No zero-hit conversations in this window."))
      return
    }

    ids.slice(0, 12).forEach((id) => {
      const badge = document.createElement("span")
      badge.className = statusBadgeClass("secondary")
      badge.textContent = `#${id}`
      this.conversationIdsTarget.append(badge)
    })
  }

  renderRows(target, rows) {
    target.innerHTML = ""
    if (rows.length === 0) {
      target.append(this.emptyText("No escalation signals in this window."))
      return
    }

    rows.forEach((row) => {
      const item = document.createElement("div")
      item.className = "d-flex align-items-center justify-content-between gap-3"

      const label = document.createElement("span")
      label.className = "text-body-secondary small"
      label.textContent = String(row.key || "unknown").replace(/_/g, " ")

      const count = document.createElement("span")
      count.className = statusBadgeClass("secondary")
      count.textContent = String(row.count || 0)

      item.append(label, count)
      target.append(item)
    })
  }

  emptyText(text) {
    const el = document.createElement("span")
    el.className = "text-body-secondary small"
    el.textContent = text
    return el
  }
}
